import { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';
import { useModals, ModalNames } from 'components/modals';
import { isMedOnly, isRecOnly } from 'shared/core/helpers/dispensaries';
import { hasTakeoverBeenEngaged } from 'src/utils/order-type-takeover-tracking';
import { useIsOrderTypeTakeoverEnabled } from './use-is-order-type-takeover-enabled';
import { useIsAgeGatePassed } from './use-is-age-gate-passed';

/**
 * Opens the order-type takeover modal once per dispensary visit, post-age-gate.
 * Per-dispensary suppression (sessionStorage) keeps shared MobX `Cart.orderType`
 * from leaking the answered state across `/dispensary/[cName]` navigations.
 */
export function useOrderTypeTakeoverPrompt(): void {
  const { dispensary } = useDispensary();
  const UI = useUI();
  const { openModal } = useModals();
  const isAgeGatePassed = useIsAgeGatePassed(dispensary);
  // Tracks the id we've already prompted for in this hook instance so
  // Next layout reuse across navigations doesn't suppress new dispensaries.
  const promptedDispensaryIdRef = useRef<string | null>(null);

  const isTakeoverEnabled = useIsOrderTypeTakeoverEnabled(dispensary);
  const isKiosk = useObserver(() => UI.isKiosk);
  const hasMenuTypeChoice = Boolean(dispensary && !isRecOnly(dispensary) && !isMedOnly(dispensary));
  const hasOrderTypeChoice = Boolean(
    dispensary?.offerAnyPickupService && dispensary?.orderTypesConfig?.delivery?.enabled
  );
  const hasAnyChoice = hasMenuTypeChoice || hasOrderTypeChoice;
  // Primitive id so Apollo refetches with fresh object refs don't re-trigger.
  const dispensaryId = dispensary?.id ?? null;

  useEffect(() => {
    if (
      promptedDispensaryIdRef.current === dispensaryId ||
      !isTakeoverEnabled ||
      isKiosk ||
      !dispensaryId ||
      !isAgeGatePassed ||
      !hasAnyChoice
    ) {
      return;
    }
    // Read inside the effect to avoid getItem+parse on every layout render.
    if (hasTakeoverBeenEngaged(dispensaryId)) {
      return;
    }
    promptedDispensaryIdRef.current = dispensaryId;
    void openModal(ModalNames.orderTypeTakeover);
  }, [isTakeoverEnabled, isKiosk, dispensaryId, isAgeGatePassed, hasAnyChoice, openModal]);
}
